import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import AWS from 'aws-sdk'

import { STSCredential } from '@/interfaces/sts-credential'
import { File as AppFile, FileToSave } from '@/modules/File/interfaces/file'
import { AcordForm } from '@/modules/AcordForm/interfaces/acord-form'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import { getSTSCredentials, getFileViewUrl } from '@/modules/File/gql/file.gql'

@Component
export default class S3Handler extends Vue {
  stsCredential: STSCredential | null = null
  filesToUpload: File[] = []
  fileUploaded: AppFile | null = null
  filesUploaded: AppFile[] = []
  filesMap: { [key: string]: boolean } = {}

  filesInfo: FileToSave[] = []
  awsS3Prefix = process.env.VUE_APP_S3_URL
  bucketPrefix = process.env.VUE_APP_BUCKET_PREFIX
  notes = ''

  acceptFileTypes = `
    application/pdf, image/*
  `
  filesUploadedSize: number[] = []
  filePrefix = ''
  fileType = ''

  showImg = false
  previewFileSrc = ''
  previewFileType = ''

  async getSTSCredentials () {
    try {
      const result = await this.$apollo.query({
        query: getSTSCredentials
      })
      this.stsCredential = result.data.getSTSCredentials
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async uploadFileToS3 (index: number) {
    if (!this.stsCredential || this.filesToUpload.length === 0) return
    const { region, accessKeyId, secretAccessKey, sessionToken, bucket } = this.stsCredential
    const { filesToUpload } = this
    const client = new AWS.S3({
      region: region,
      accessKeyId: accessKeyId,
      secretAccessKey: secretAccessKey,
      sessionToken: sessionToken
    })

    const fileToUpload = filesToUpload[index]

    const timeStamp = (new Date()).valueOf()

    const extension = this.$R.last(fileToUpload.name.split('.'))

    const rdnSuffix = Math.floor(Math.random() * 1000)

    const storeKey = `${this.bucketPrefix}/${this.filePrefix}/${timeStamp}${rdnSuffix}.${extension}`

    this.filesMap[fileToUpload.name] = true

    const params = {
      ACL: 'private',
      Bucket: bucket,
      Key: storeKey,
      Body: fileToUpload
    }

    client
      .putObject(params)
      .on('httpUploadProgress', (evt) => {
        this.filesUploadedSize[index] = evt.loaded
      })
      .send((err, data) => {
        if (err) {
          showErrors({
            msg: 'File uploading failed! Error:' + err,
            type: SnackType.Error
          })
        } else {
          if (this.filesMap[fileToUpload.name]) {
            this.filesInfo.push({
              url: this.awsS3Prefix + '/' + storeKey,
              name: fileToUpload.name,
              size: fileToUpload.size,
              type: this.fileType,
              notes: this.notes
            })
            delete this.filesMap[fileToUpload.name]
          }
        }
      })
  }

  async getFileUrl (fileId: number) {
    try {
      const result = await this.$apollo.query({
        query: getFileViewUrl,
        variables: {
          fileId: Number(fileId)
        }
      })
      return result.data.getFileViewUrl
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async downloadFile (file: AppFile | string) {
    if (!this.checkCredentialValid()) {
      await this.getSTSCredentials()
    }

    showNotification({
      msg: 'File downloading will start soon, please wait!',
      type: SnackType.Success
    })

    if (typeof file === 'string') {
      const fileUrl = file
      const fileName = this.$R.last(fileUrl.split('/'))
      await this.downloadFileFromS3(fileUrl, fileName)
    } else {
      const fileUrl = await this.getFileUrl(file.id)
      await this.downloadFileFromS3(fileUrl, file.name)
    }
  }

  async downloadAcordForm (acordForm: AcordForm, isLocked: boolean) {
    if (!this.checkCredentialValid()) {
      await this.getSTSCredentials()
    }
    const acordFormUrl = isLocked ? acordForm.lockedUrl : acordForm.url
    showNotification({
      msg: 'File downloading will start soon, please wait!',
      type: SnackType.Success
    })
    await this.downloadFileFromS3(acordFormUrl, acordForm.name)
  }

  async downloadFileFromS3 (fileUrl: string, fileName: string) {
    const blob = await this.getS3FileBlob(fileUrl)
    if (blob) {
      this.saveFile(blob, fileName)
    }
  }

  async previewFile (file: AppFile) {
    if (!this.checkCredentialValid()) {
      await this.getSTSCredentials()
    }
    const fileUrl = await this.getFileUrl(file.id)
    await this.previewFileFromS3(fileUrl, file.name)
  }

  async previewFileFromS3 (fileUrl: string, fileName: string) {
    const blob = await this.getS3FileBlob(fileUrl)
    if (blob) {
      this.showFile(blob, fileName)
    }
  }

  async getS3FileBlob (fileUrl: string):Promise<Blob | null> {
    if (!this.stsCredential || !this.awsS3Prefix) return null
    const { region, accessKeyId, secretAccessKey, sessionToken, bucket } = this.stsCredential

    const storeKey = this.$R.last(fileUrl.split(this.awsS3Prefix + '/')) || ''
    if (!storeKey) {
      showNotification({
        msg: 'Downloading failed!',
        type: SnackType.Error
      })
      return null
    }
    const client = new AWS.S3({
      region: region,
      accessKeyId: accessKeyId,
      secretAccessKey: secretAccessKey,
      sessionToken: sessionToken
    })
    try {
      const file: any = await client
        .getObject({ Bucket: bucket, Key: storeKey })
        .promise()
      const blob = new Blob([new Uint8Array(file.Body)])
      return blob
    } catch (error) {
      showNotification({
        msg: 'Downloading failed! Error:' + error,
        type: SnackType.Error
      })
      return null
    }
  }

  showFile (blob: Blob, fileName: string) {
    this.previewFileSrc = window.URL.createObjectURL(blob)
    this.previewFileType = this.$R.last(fileName.split('.'))
    this.showImg = true
  }

  saveFile (blob: Blob, fileName: string) {
    var link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = fileName
    link.click()
  }

  checkCredentialValid () {
    if (!this.stsCredential) return false
    return this.$moment().isBefore(this.$moment.utc(this.stsCredential.expiration).local())
  }

  clearFile () {
    this.filesInfo = []
    this.filesToUpload = []
    this.filesUploadedSize = []
    this.filesMap = {}
  }
}
